import React from "react";
import {Checkbox, Tooltip, Typography} from "@mui/material";
import {PermIdentity} from "@mui/icons-material";
import {PlatformTable} from "../PlatformTable";
import {withAppContext} from "../../util/withAppContext";
import withNavigate from "../../util/useNavigate";
import withBreakpoint from "../../util/withBreakpoint";
import {Black, Blue3, Grey6, Grey7} from "../../theme/styles";
import {formatLocalDate} from "../../util/Formatters";
import OwnerHierarchyDrawer from "./OwnerHierarchyDrawer";
import {formatLocal2} from "../../util/Formatters";
import {classificationByCode} from "../../util/objectClassifications";
import {statusPill} from "../../util/statusPill";
import {makeCresAxios} from "../../util/Axios";
import ArtefactPills from "./ArtefactPills";
import ProjectContents from "../Project/ProjectContents";
import ObjectIdLink, {RecordDrawerContext} from "../Project/ObjectIdLink";
import RecordDrawer from "./RecordDrawer";

class ObjectRevisionTable extends PlatformTable {

    getCellRenderer(name) {
        if (name === "Object Number Cell") {
            return this.renderObjectNumberCell;
        }
        if (name === "Status Pill Cell") {
            return this.renderStatusPillCell;
        }
        if (name === "Local Datetime 2 Cell") {
            return this.renderLocalDatetime2Cell;
        }
        if (name === "Owner Position Cell") {
            return this.renderOwnerPositionCell;
        }
        if (name === "Record Artefacts Cell") {
            return this.renderArtefactsCell;
        }
        if (name === "Project Contents Cell") {
            return this.renderProjectContentsCell;
        }
        if (name === "Object Id Link Cell") {
            return this.renderObjectIdLinkCell;
        }
        if (name === "Ask Selection Cell") {
            return this.renderAskSelectionCell;
        }

        return super.getCellRenderer(name);
    }

    // A METHOD, NOT A FIELD, so `super` reaches PlatformTable's own lookup. Written as an arrow
    // field first, which threw "getHeaderRenderer is not a function" on every header in the grid:
    // a class field cannot see the superclass's field through `super`, and it shadows it entirely.
    // A TICKED ROW AND THE CURRENT RECORD ARE TWO DIFFERENT THINGS and must not look alike. Ed:
    // "Why is the first row painted background grey" -- because the EC marks row 0 as its current
    // record on every query and PlatformTable painted that grey, which read as "selected" beside a
    // checkbox saying the opposite. Adding tick-selection to a grid that already had a
    // current-record highlight put two meanings on one visual state.
    //
    // So the grey now means TICKED, which is the meaning a reader of this page acts on, and the
    // current record keeps the blue outline index.css already carried as its documented rollback.
    rowClassName(params) {
        const {usid} = this.rowTarget(params?.row?.id);
        const ticked = usid && this.props.selectedUSIDs?.has?.(usid) === true;
        // BOTH, WHERE BOTH ARE TRUE. A row can be the current record AND ticked, and suppressing
        // one would make the state depend on which happened first.
        return [ticked ? "cc-row-ticked" : "", super.rowClassName(params)]
            .filter(Boolean).join(" ");
    }

    getHeaderRenderer(name) {
        if (name === "Ask Selection Header") {
            return this.renderAskSelectionHeader;
        }

        return super.getHeaderRenderer(name);
    }

    // The USID and detail route for one grid row, or nulls. Reads the EC buffer rather than the grid
    // row, because the grid carries only the displayed columns and the route is derived from
    // ObjectClassification, which is not one of them.
    rowTarget(rowId) {
        const row = this.state.buffer && this.state.buffer[rowId];
        if (!row) return {usid: null, route: null};

        const usid = row.get ? row.get("USID") : row.USID;
        // From the ROW's own classification, not the page's: one grid serves all nine, and a
        // relationship can put a record of another kind in this list.
        const code = row.get ? row.get("ObjectClassification") : row.ObjectClassification;

        return {usid, route: classificationByCode(code)?.route ?? null};
    }

    // Single-click a row to open its detail page. The action-menu button stops propagation, so the
    // menu still opens without navigating.
    onRowClick(params) {
        const {usid, route} = this.rowTarget(params.row.id);
        // PICKING, NOT OPENING. A host that embeds this grid as a chooser (the Purchase Orders
        // picker on a contract) wants a click to tick the row; navigating away would close the
        // dialog it is in. The checkbox still works; this is the row itself.
        if (this.props.pickOnClick) {
            if (usid) this.props.onToggleSelect?.(usid);
            return;
        }
        if (usid && route && this.props.navigate) {
            this.props.navigate(`${route}/${usid}`);
            return;
        }

        // Unresolvable row: fall back to the base behaviour (select the record) rather than
        // swallowing the click.
        super.onRowClick(params);
    }

    // A tick box, so a question can be asked of a chosen set of records rather than of the whole
    // list. The selection itself lives on the PAGE, not here: it has to survive paging and the
    // toolbar that acts on it sits above this grid, so a Set held in this component's state would be
    // two sources of truth for one set. This cell reads props and reports a toggle.
    //
    // THE CLICK IS STOPPED. A row click navigates to the record (onRowClick above), so without this
    // ticking a box would open the document instead of selecting it.
    // THE HEADER OF THE TICK COLUMN, which until now was empty -- and an unheaded column of
    // checkboxes beside a button saying "tick up to 25 records" is a puzzle, not an instruction.
    // Ed asked "where do I do this?" and the honest answer was that nothing on screen connected
    // the two.
    //
    // A HEADER CHECKBOX IS THE ANSWER BY CONVENTION. Every grid anybody has used puts select-all
    // there, so it labels the column by being the thing people already look for -- which beats any
    // label that would fit in 44px. Checked when every shown row is ticked, indeterminate when
    // some are: that is the state a reader uses to tell "I ticked these four" from "I ticked the
    // page".
    renderAskSelectionHeader = (___, params, __, _) => {
        const shown = (this.state.buffer || []).map(row => (row?.get ? row.get("USID") : row?.USID))
                                               .filter(one => !!one);
        const selected = shown.filter(one => this.props.selectedUSIDs?.has?.(one) === true);
        const all = shown.length > 0 && selected.length === shown.length;
        return (
            // 37px AND NOT height:100%, which is the difference between a header cell and a body
            // cell and the reason this sat high. A ROW has a definite 48px, so a cell renderer can
            // claim height:100% and centre inside it -- which is what ArtefactPills does and what
            // this copied. A HEADER cell's container has no definite height, so a percentage
            // collapses to the content's own height, alignItems centres the box against ITSELF,
            // and the result sits at the top of the header.
            //
            // 37px is what renderStandardSingleHeader hardcodes for exactly this reason, so taking
            // the same number puts the checkbox on the same line as the column titles beside it
            // rather than merely somewhere less wrong.
            //
            // AND 3px TO THE RIGHT, which is an offset and not a centring. Both this and the row
            // checkbox below are centred in the same 44px column, so they ought to line up and do
            // not: a DataGrid header cell and a body cell carry different horizontal padding, so
            // "centred" resolves ~3px apart. translateX rather than padding or margin because with
            // justifyContent:center either of those would shrink the box and move the content by
            // HALF what you asked for -- this moves it by exactly the number written down.
            //
            // Measured on screen by Ed, 2026-10-22, against the column of row checkboxes beneath.
            <div style={{display: "flex", alignItems: "center", justifyContent: "center",
                         height: "37px", transform: "translateX(3px)"}}>
                <Tooltip title={all ? "Untick every record on this page"
                                    : `Tick every record on this page (${shown.length})`}>
                    <Checkbox size="small" checked={all}
                              indeterminate={selected.length > 0 && !all}
                              disabled={shown.length === 0}
                              onChange={() => (all ? this.props.onClearSelection?.()
                                                   : this.props.onSelectAllShown?.())}
                              inputProps={{"aria-label": "select every record on this page"}}
                              sx={{p: 0.25, color: Grey6, "&.Mui-checked": {color: Blue3},
                                   "&.MuiCheckbox-indeterminate": {color: Blue3}}}/>
                </Tooltip>
            </div>
        );
    }

    renderAskSelectionCell = (___, params, __, _) => {
        // FROM THE EC BUFFER, NOT params.row, and that distinction is why this checkbox has never
        // once rendered. The grid row carries only the DISPLAYED columns and USID is not one of
        // them -- see the Columns list on the applet -- so `params.row.USID` was undefined on
        // every row, the guard below returned null, and the column drew nothing.
        //
        // IT FAILED SILENTLY AND TOOK THE WHOLE FEATURE WITH IT. With no row checkbox there was
        // no way to select anything, so the ask button that only appeared once something was
        // selected could never appear either -- two faults that each hid the other. rowTarget
        // exists for exactly this and says so in its own comment; it was sitting one method away.
        const {usid} = this.rowTarget(params?.row?.id);
        if (!usid) return null;
        const selected = this.props.selectedUSIDs?.has?.(usid) === true;
        return (
            <div style={{display: "flex", alignItems: "center", justifyContent: "center",
                         height: "100%"}}
                 onClick={(e) => e.stopPropagation()}>
                <Checkbox size="small" checked={selected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => this.props.onToggleSelect?.(usid)}
                          inputProps={{"aria-label": "select this record to ask about"}}
                          sx={{p: 0.25, color: Grey6, "&.Mui-checked": {color: Blue3}}}/>
            </div>
        );
    }

    // The object number is the row's link into the detail page. Keyed on the REVISION USID, because
    // that is what the detail route addresses -- the logical object USID would not identify which
    // revision to show.
    // THE IDENTIFIER IS TWO DOORS, on the card and in the column alike (Ed, 2026-10-30, on the
    // Items list: "I still am not getting the right side drawer when I click on the Object ID"):
    // the number opens the record drawer beside the list, the icon beside it opens the record in a
    // new window. Clicking the ROW still opens the record page, as it always has.
    //
    // THE SAME params.row TRAP AS THE CHECKBOX: USID is not a displayed column, so it is read off
    // the buffer through rowTarget rather than off params.row. A row the buffer cannot place draws
    // the number plain.
    renderObjectNumberCell = (___, params, __, _) => {
        // FROM THE BUFFER, NOT params.row: a card's row carries only its placed columns, so the
        // number is missing there and drew "—" on every card the first time this was written.
        const buffered = this.state.buffer && this.state.buffer[params?.row?.id];
        const number = buffered?.get ? buffered.get("ObjectID") : buffered?.ObjectID;
        const {usid} = this.rowTarget(params?.row?.id);
        return (
            <div style={{display: "flex", alignItems: "center", height: "100%"}}>
                <ObjectIdLink id={number || null} revisionUSID={usid} size={13} weight={500}/>
            </div>
        );
    };

    // The card's title: the same control at the card's size.
    renderObjectIdLinkCell = (___, params, __, _) => {
        const buffered = this.state.buffer && this.state.buffer[params?.row?.id];
        const number = buffered?.get ? buffered.get("ObjectID") : buffered?.ObjectID;
        const {usid} = this.rowTarget(params?.row?.id);
        return <ObjectIdLink id={number || null} revisionUSID={usid} size={15} weight={500}/>;
    };

    // The built-in "Local Datetime Cell" formats with formatLocal; this one uses formatLocal2, which
    // renders "Sun Aug 09, 2026 06:14:32 PM PDT" and appends the viewer's zone. Otherwise identical,
    // including greying the value while a background fetch is in flight so a stale row reads as stale.
    renderLocalDatetime2Cell = (___, params, __, _) => {
        const backgroundFetch = this.state.fetchBackgroundOperation;
        const buffer = backgroundFetch ? this.state.previousBuffer : this.state.buffer;
        const colour = backgroundFetch ? Grey7 : Black;

        // The buffer can be shorter than the grid during a refresh; indexing past it throws.
        if (!buffer || params.row.id > buffer.length - 1) return null;

        // Buffer rows are Maps, not plain objects.
        const value = buffer[params.row.id].get(params.field);
        const content = value === null || value === undefined ? "--" : formatLocal2(value);

        return (
            <div style={{whiteSpace: "nowrap", width: "100%", display: "block", color: colour, height: "100%", overflow: "hidden", textOverflow: "ellipsis", alignContent: "center"}}>
                <Typography style={{fontSize: "14px"}}>{content}</Typography>
            </div>
        );
    };

    // The owner whose organisation chart is open, or null. Held here rather than per cell so only
    // one drawer exists no matter how many rows are on screen.
    state = {...this.state, ownerHierarchyFor: null, ownerHierarchyUSID: null};

    // THE PRIMARY POSITION, drawn with the person glyph t2 uses for an identity. The column reads
    // "who this record belongs to", and a Position is a seat somebody occupies rather than a
    // department -- the icon says that before the name is read.
    //
    // Stewardship is the joined POSITION.NAME, not a second lookup: ObjectRevision already left-joins
    // POSITION on PrimaryPositionUSID, so the name arrives with the row.
    //
    // A record can legitimately have NO primary position -- one created under a Manager or
    // Organization persona gets none, deliberately, because those are not ownership. Those draw the
    // dash every other column uses for absent rather than an icon with nothing beside it, which
    // would read as a broken row.
    renderOwnerPositionCell = (___, params, __, _) => {
        const row = params?.row;
        const name = (row?.get ? row.get("PrimaryPositionName") : row?.PrimaryPositionName) || "";

        // THE USID COMES FROM THE BUFFER, NOT THE GRID ROW. The grid carries only the columns being
        // displayed, and PrimaryPositionUSID is not one of them -- so reading it from params.row
        // gave undefined, the drawer had no position to look up, and it sat on "Loading" forever.
        // The name worked because Stewardship IS displayed, which is what made the bug look like a
        // loading problem rather than a missing field. Same reason rowTarget reads the buffer.
        const buffered = this.state.buffer && this.state.buffer[params?.row?.id];
        const usid = buffered?.get ? buffered.get("PrimaryPositionUSID") : undefined;

        if (!name) {
            return (
                <div style={{display: "flex", alignItems: "center", height: "100%", color: Grey7}}>
                    <Typography style={{fontSize: "14px"}}>--</Typography>
                </div>
            );
        }

        // THE NAME IS THE LINK, not a second icon beside it. It is a far larger target than an 18px
        // glyph, and linking the noun is how links work everywhere -- you click "Facilities Manager"
        // to find out about Facilities Manager. The icon stays as a type marker so the pair reads
        // "position: <name>", which is what says the link is about a seat rather than this record.
        //
        // STOPPROPAGATION, because a row click opens the record. Without it the drawer would open
        // and the page would navigate out from under it. The action-menu button does the same.
        //
        // A button element rather than a styled div, so it is reachable by keyboard.
        return (
            <div style={{display: "flex", alignItems: "center", gap: 8, height: "100%",
                         overflow: "hidden"}}>
                <PermIdentity style={{fontSize: 18, color: Grey7, flexShrink: 0}}/>
                <Typography
                    component="button"
                    type="button"
                    title="Show where this position sits"
                    onClick={event => {
                        event.stopPropagation();
                        this.setState({ownerHierarchyFor: name, ownerHierarchyUSID: usid});
                    }}
                    style={{fontSize: "14px", color: Blue3, cursor: "pointer", overflow: "hidden",
                            textOverflow: "ellipsis", whiteSpace: "nowrap", background: "none",
                            border: "none", padding: 0, textAlign: "left", font: "inherit"}}
                >{name}</Typography>
            </div>
        );
    };

    renderStatusPillCell = (___, params, __, _) => {
        // STATUS THROUGH readAttribute LIKE THE OTHER TWO, which this did not do and which the
        // comment below has warned about since the day it was written -- for IsCurrent and
        // EffectiveDatetime, while the status itself was still read straight off `params.row`.
        //
        // IT DID NOT MATTER WHILE THIS ONLY EVER RAN IN A GRID, because Status is a placed column
        // on every one of these lists. In CARD VIEW exactly one synthetic column is placed, so the
        // row carries nothing: `params.row.Status` is undefined, the pill fell back to "" and the
        // card drew an empty chip where the grid drew RELEASED. Found by making cards the default
        // and running the suite, not by reading it.
        const status = this.readAttribute(params, "Status") || "";

        // TWO ROWS READING "RELEASED" AND ONLY ONE OF THEM GOVERNING.
        //
        // Releasing on the 1st to take force on the 15th is the training window, so a revision can
        // be released and not yet in force while its predecessor keeps IS_CURRENT and keeps
        // governing. The database permits that deliberately -- UOBJECT_REVISION_CURRENT constrains
        // IS_CURRENT, nothing constrains STATUS -- and `Promote Revisions By Effective Date` hands
        // over when the date arrives.
        //
        // The LIST said none of it. Ed, on TC-00002: "I am seeing two TC-00002 records in released
        // status." Two identical green pills, one live and one scheduled, is indistinguishable from
        // the duplicate anybody would reasonably assume it was. The detail page has carried a
        // notYetInForce banner for this since the released/effective split landed; the list had
        // nothing.
        //
        // READ THROUGH readAttribute, NOT params.row. getRows writes only the PLACED columns, so
        // IsCurrent and EffectiveDatetime are undefined on the row unless somebody happens to be
        // showing them -- and the marker would then simply never appear, silently, which is the
        // failure mode readAttribute exists to prevent.
        if (status === "RELEASED") {
            const isCurrent = this.readAttribute(params, "IsCurrent");
            const effective = this.readAttribute(params, "EffectiveDatetime");
            // AN EXPLICIT FALSE, not merely falsy. undefined or null means the buffer could not
            // answer, and treating that as "not current" would mark every released revision as
            // scheduled. Compared as a STRING because the value arrives from the EC rather than
            // from JSON -- a BOOLEAN attribute can surface as "false", and a strict `=== false`
            // then silently never matched, which is how this shipped doing nothing the first time.
            const notCurrent = isCurrent !== undefined && isCurrent !== null
                && String(isCurrent).toLowerCase() === "false";
            if (notCurrent && effective && new Date(effective) > new Date()) {
                return (
                    <span style={{display: "inline-flex", alignItems: "center", gap: 6}}>
                        {statusPill(status)}
                        <span style={{fontSize: 12, color: Grey6, whiteSpace: "nowrap"}}>
                            in force {formatLocalDate(effective)}
                        </span>
                    </span>
                );
            }
        }

        return statusPill(status);
    };

    // WHICH ROWS CARRY A BILL OF MATERIALS OR DOCUMENTS, fetched ONCE for the page.
    //
    // NEVER PER ROW. Forty rows times two artefact kinds is eighty round trips on the busiest
    // screen in the product, so the whole page's revisions go up as one parameter and come back as
    // one map. The cell renderer then reads a map that is already in hand.
    //
    // KEYED ON THE ROW SET, so this does not refetch on every setState the grid makes -- and it
    // makes several per query. A new page, a filter, a sort: new identifiers, new key, one call.
    componentDidUpdate(previousProps, previousState) {
        if (super.componentDidUpdate) {
            super.componentDidUpdate(previousProps, previousState);
        }

        const identifiers = (this.state.buffer || [])
            .map(row => (row && row.get ? row.get("USID") : row && row.USID))
            .filter(Boolean);
        const key = identifiers.join(",");

        if (!key || key === this.artefactKey) return;
        this.artefactKey = key;

        makeCresAxios(this.props.appContext?.sessionContext)
            .get("t1/v1/record_artefacts", {params: {RevisionUSIDs: key}})
            // A COLUMN THAT CANNOT BE FILLED IS LEFT EMPTY, never errored. The grid is the busiest
            // screen here and it works perfectly without this; a failure to decorate it must not
            // become a failure to draw it.
            .then(response => this.setState({artefacts: response?.data?.Artefacts ?? {}}))
            .catch(() => this.setState({artefacts: {}}));

        // WHAT EACH PROJECT HOLDS, for the Projects list's cards (Ed, 2026-10-29): the counts ride
        // on the project's own door, one read for every project the reader reaches rather than
        // one per card, on the same key as the artefacts so it goes up once per page. Only on a
        // Projects list -- every other classification has no door that answers this.
        if (this.props.classification === "Project") {
            makeCresAxios(this.props.appContext?.sessionContext)
                .get("t1/v1/projects", {params: {Limit: 500}, headers: {"idoc-response": "v1.ProjectRevision-001"}})
                .then(response => {
                    const held = response?.data?.PlatformMessage?.ListOfProjectRevision?.ProjectRevision;
                    const rows = held == null ? [] : (Array.isArray(held) ? held : [held]);
                    const facts = {};
                    for (const row of rows) if (row?.ObjectUSID) facts[String(row.ObjectUSID)] = row;
                    this.setState({projectFacts: facts});
                })
                .catch(() => this.setState({projectFacts: {}}));
        }
    }

    // THE FIVE COUNTS IN THE CARD'S CORNER, where the artefact pills sit on every other list: the
    // thing a reader scanning projects wants to know without opening one is what is in them.
    // Nothing until the facts arrive, rather than five zeros that would read as empty folders.
    renderProjectContentsCell = (___, params, __, _) => {
        const buffered = this.state.buffer && this.state.buffer[params?.row?.id];
        const objectUSID = buffered?.get ? buffered.get("ObjectUSID") : buffered?.ObjectUSID;
        const found = objectUSID && (this.state.projectFacts || {})[String(objectUSID)];
        if (!found) return null;
        return <ProjectContents counts={found.Counts}/>;
    };

    // A PILL PER ARTEFACT THE ROW HAS, and nothing at all where it has neither. Most records in a
    // quality system are SOPs and training records that will never carry either, and a column that
    // draws something on every row teaches people to stop reading it.
    renderArtefactsCell = (___, params, __, _) => {
        // THE SIGNATURE IS FOUR ARGUMENTS AND params IS THE SECOND, which is not guessable and is
        // why the first version of this threw on every row and took the whole grid down with it.
        // Every renderer in this file has the same shape; copy one rather than assume.
        //
        // AND THE USID COMES FROM THE BUFFER. The grid carries only the columns being displayed
        // and USID is not one of them, so reading it off params.row gives undefined -- the same
        // trap the owner cell above documents.
        const buffered = this.state.buffer && this.state.buffer[params?.row?.id];
        const usid = buffered?.get ? buffered.get("USID") : buffered?.USID;
        const found = usid && (this.state.artefacts || {})[usid];
        if (!found) return null;

        return <ArtefactPills sbom={!!found.SBOM} documents={!!found.Documents}
                              attachments={found.Attachments}
                              revisionUSID={usid}
                              sessionContext={this.props.appContext?.sessionContext}/>;
    };

    // ONE drawer for the whole grid, not one per row.
    render() {
        return (
            <>
                {/* ONE RECORD DRAWER PER LIST, provided to every identifier the table draws. */}
                <RecordDrawerContext.Provider value={revisionUSID => this.setState({recordDrawer: revisionUSID})}>
                    {super.render()}
                </RecordDrawerContext.Provider>
                <RecordDrawer revisionUSID={this.state.recordDrawer ?? null}
                              sessionContext={this.props.appContext?.sessionContext}
                              onClose={() => this.setState({recordDrawer: null})}/>
                <OwnerHierarchyDrawer
                    open={!!this.state.ownerHierarchyFor}
                    positionName={this.state.ownerHierarchyFor}
                    positionUSID={this.state.ownerHierarchyUSID}
                    onClose={() => this.setState({ownerHierarchyFor: null, ownerHierarchyUSID: null})}
                />
            </>
        );
    }
}

export default withNavigate(withAppContext(withBreakpoint(ObjectRevisionTable)));
